import type { InboundInit } from '@/types/bridge';
import type { TLang } from '@/i18n';
import { ESolutionCode } from '@/types';

/** marketing=1 仅切换调试门店，是否展示入口仍由资格接口决定。 */
const DEV_MARKETING_STORE_ID = 'S20240917151700007210';

/**
 * 仅开发环境：从 URL query 驱动 init，便于直接打开 http://localhost:3000/ 联调。
 *
 * 线上不会走到：生产构建下 webpack 把 `process.env.NODE_ENV` 替换为字面量
 * 'production'，下面的判断恒真而直接 return，Terser 会剔除其余逻辑。
 *
 * 用法：
 *   ?debug=1                        使用默认门店，入口由资格接口决定
 *   ?debug=1&marketing=1            使用营销调试门店，入口由资格接口决定
 *   ?debug=1&marketing=1&language=en-US
 *   ?debug=1&storeId=xxx&token=xxx  任意字段单独覆盖
 */

/** 与 src/index.tsx 里原有 MOCK 注释块保持一致的本地默认值 */
const DEV_DEFAULTS = {
  token:
    'eyJ6aXAiOiJERUYiLCJhbGciOiJFUzI1NiJ9.eJwdjEEOgjAQRe8yaxbttNNOOYQrLlBbaiCgWJCYGO_ihlt4He5hZfXzX17eC_qlgxrQeR-DYRWc0Ciss5Ysy4iaiBQnqKB9TlBLy0zMRukKOr8cgJwzB_BTMSD4qKLSUvmQNJN0OnE4R2ox2tB6WVKPORfRoCGN5c7LBWpRwS2XhebUIEqh5DpMYz-4YNexFz7dBxVN7kxy_0K-FnX_fvZtg_cPJlc4Wg.8Wa3Rgmg90uJoPGhNXdvo6vlZWWaF251282f_CNHPWL33nkhzMWkY81oQkWPeYUweoINKcnOFxO083nfjUgfwg',
  tenantCode: 'TNT221031vlpmjl9c7vmj0afql3d6ri6f9',
  storeId: 'S002605082410036635',
  orgId: 'O20241007172800004204',
  bizType: 'restaurant',
  solution_code: ESolutionCode.BOSS_REPORT as string,
  source: 'GPOS_WEB',
  language: 'zh-CN' as TLang,
  email: 'ye.tao@sunmi.com',
  uid: '373275',
  phone: '18937107705',
  // 本工程无来源，仅为联调时能看到非空值
  timezone: 'Asia/Bangkok',
  currency: 'THB',
  roles: 'merchant_owner',
};

/**
 * 读取本地调试用的 init payload；未开启调试模式（或生产构建）时返回 null。
 */
export function readDevInitFromQuery(): InboundInit['payload'] | null {
  if (process.env.NODE_ENV === 'production') return null;
  if (typeof window === 'undefined') return null;

  const q = new URLSearchParams(window.location.search);
  if (q.get('debug') !== '1') return null;

  const pick = (key: string, fallback: string): string => q.get(key) ?? fallback;

  // marketing=1 只选择调试门店，不绕过资格接口。
  const storeIdDefault =
    q.get('marketing') === '1' ? DEV_MARKETING_STORE_ID : DEV_DEFAULTS.storeId;

  return {
    token: pick('token', DEV_DEFAULTS.token),
    tenantCode: pick('tenantCode', DEV_DEFAULTS.tenantCode),
    storeId: pick('storeId', storeIdDefault),
    orgId: pick('orgId', DEV_DEFAULTS.orgId),
    bizType: pick('bizType', DEV_DEFAULTS.bizType),
    solution_code: pick('solution_code', DEV_DEFAULTS.solution_code),
    source: pick('source', DEV_DEFAULTS.source),
    language: pick('language', DEV_DEFAULTS.language) as TLang,
    email: pick('email', DEV_DEFAULTS.email),
    uid: pick('uid', DEV_DEFAULTS.uid),
    phone: pick('phone', DEV_DEFAULTS.phone),
    timezone: pick('timezone', DEV_DEFAULTS.timezone),
    currency: pick('currency', DEV_DEFAULTS.currency),
    roles: pick('roles', DEV_DEFAULTS.roles),
    config: {
      history: true,
      fullScreen: true,
      // 直接打开（无父页面）时 close 会 postMessage 给不存在的父窗口，默认关掉
      close: q.get('close') === '1',
    },
  };
}
